import { useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

/** Gap below the measured chrome header. Originally baked into `ui2026:top-20` with `p-1`. */
const WORKSPACE_CHROME_HEADER_BOTTOM_GAP_PX = 12;

const WORKSPACE_CHROME_HEADER_SELECTOR = '[data-workspace-chrome-header]';
const WORKSPACE_CHROME_ROOT_SELECTOR = '[data-workspace-chrome-root]';

/**
 * Viewport `top` for fixed panels that should sit below workspace banners and the header.
 * Returns `null` when disabled or the header is not mounted.
 */
export const useWorkspaceChromeHeaderBottom = (isEnabled: boolean): number | null => {
  const [top, setTop] = useState<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (!isEnabled) {
      setTop(null);
      return;
    }

    const measure = () => {
      const header = document.querySelector(WORKSPACE_CHROME_HEADER_SELECTOR);

      if (!header) {
        setTop(null);
        return;
      }

      const nextTop = header.getBoundingClientRect().bottom + WORKSPACE_CHROME_HEADER_BOTTOM_GAP_PX;

      setTop((current) => (current === nextTop ? current : nextTop));
    };

    const chromeRoot = document.querySelector(WORKSPACE_CHROME_ROOT_SELECTOR);

    if (!chromeRoot) {
      measure();
      window.addEventListener('resize', measure);

      return () => {
        window.removeEventListener('resize', measure);
      };
    }

    const observer = new ResizeObserver(measure);
    // The layout unmounts the header (e.g. while the right panel is open) without resizing the root.
    const mutationObserver = new MutationObserver(measure);

    observer.observe(chromeRoot);
    mutationObserver.observe(chromeRoot, { childList: true });
    window.addEventListener('resize', measure);
    measure();

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isEnabled]);

  return top;
};
